import * as React from 'react';
import { useCallback } from 'react';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CustomButton from '../button';

import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';
import { codeAPI, userAPI, authorizeAPI, settingAPI } from '../../actions/imsServer';
import * as jwt from '../../actions/jwt';
import { useDispatch } from 'react-redux';
import CustomSelect from '../select';

import closeIcon from '../../assets/images/close.png';
import offIcon from '../../assets/images/check/off.png';
import onIcon from '../../assets/images/check/on.png';
import phoneIcon from '../../assets/images/phone.png';
import googleIcon from '../../assets/images/google.png';
import appleIcon from '../../assets/images/apple.png';
import messageIcon from '../../assets/images/message.png';

// Password validation util
function isValidPassword(pw) {
  const passwordRegex = /^(?!^\d+$)(?!^[a-zA-Z]+$)(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$)[a-zA-Z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{6,12}$/;
  return passwordRegex.test(pw);
}

export const SignUp = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [email, setEmail] = React.useState(location?.state?.snsData?.email || '');  
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [auth, setAuth] = React.useState('');
  const [agree, setAgree] = React.useState(false);
  const [agreeSns, setAgreeSns] = React.useState(false);
  const [password, setPassword] = React.useState(''); 
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [passwordConfirm, setPasswordConfirm] = React.useState('');
  const [passwordConfirmError, setPasswordConfirmError] = React.useState(false);
  const [passwordConfirmErrorMessage, setPasswordConfirmErrorMessage] = React.useState('');
  const [userName, setUserName] = React.useState(location?.state?.snsData?.name || '');
  const [userNameError, setUserNameError] = React.useState(false);
  const [userNameErrorMessage, setUserNameErrorMessage] = React.useState('');  
  const [userNameRequired, setUserNameRequired] = React.useState(true);
  const [snsId, setSnsId] = React.useState(location?.state?.snsData?.snsId || '');
  const [snsType, setSnsType] = React.useState(location?.state?.snsData?.snsType || '');  
  const [loginType, setLoginType] = React.useState(location?.state?.type || 'email');
  const [step, setStep] = React.useState(location?.state?.step || 0);
  const type = ['setting_terms', 'privacy_title', 'privacy_third_party_title', 'privacy_marketing_title'];
  
  // 전화번호 관련 상태 추가
  const [countryCode, setCountryCode] = React.useState('81');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [phoneError, setPhoneError] = React.useState(false);
  const [phoneErrorMessage, setPhoneErrorMessage] = React.useState('');
 
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [countdown, setCountdown] = React.useState(0);
  const [hasSentCode, setHasSentCode] = React.useState(false);
  const [platform, setPlatform] = React.useState('web');
  const [pushToken, setPushToken] = React.useState('');



  React.useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [countdown]);

  // 플랫폼 정보 가져오기
  React.useEffect(() => {

    const originalConsoleLog = console.log;
    console.log = (...args) => {
        originalConsoleLog.apply(console, args);
        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'console',
                data: args
            }));
        }
    };

    // Notify Main.js that signup page is loaded
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'pageLoaded'
      }));
    }

    // Get platform information from React Native
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'getPlatform'
      }));
    }

    // Listen for push token event
    const handlePushToken = (event) => {
      const token = event.detail;
      setPushToken(token);
      // localStorage에도 저장
      localStorage.setItem('pushToken', token);
    };

    // Listen for message events
    const handleMessage = (event) => {     
      try {
        // Skip webpack hot reload messages and other non-JSON messages
        if (typeof event.data === 'string' && (
          event.data.startsWith('webpackHot') || 
          event.data.startsWith('webpack') ||
          event.data.startsWith('{') === false
        )) {
          return;
        }
        
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;        

        if (data.type === 'platformInfo') {
          // Set platform information received from React Native
          setPlatform(data.platform);
        } else if (data.type === 'googleLoginResult' || data.type === 'appleLoginResult') {          
          if (data.success && data.data) {
            // 구글/애플 로그인 성공 시 자동 로그인 처리
            const loginData = data.data;            
            
            // sns 로그인 처리 함수 호출
            handleSNSLogin(loginData);
          } else {
            // 구글/애플 로그인 실패      
            setSnackbar({
              open: true,
              message: t('auth_sns_login_failed'),
              severity: 'error'
            });            
          }
        }
      } catch (e) {
        // Only log errors for actual JSON parsing issues, not webpack messages
        if (!event.data || typeof event.data !== 'string' || !event.data.startsWith('webpack')) {
          console.error('Error handling message:', e);
        }
      }
    };

    // Add event listeners
    window.addEventListener('pushTokenReceived', handlePushToken);
    window.addEventListener('message', handleMessage);
    
    // Cleanup
    return () => {
      window.removeEventListener('pushTokenReceived', handlePushToken);
      window.removeEventListener('message', handleMessage);

      console.log = originalConsoleLog;
    };
  }, []);

  const handleSubmit = (event) => {
    if (userNameError || emailError || passwordError) {
      event.preventDefault();
      return;
    }   
  };
  
  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);
    if (!value || !/\S+@\S+\.\S+/.test(value)) {
      setEmailError(true);
      setEmailErrorMessage(t('auth_invalid_email'));
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }
  };

  const handleClearEmail = () => setEmail('');

  const handleAuthChange = (event) => {
    const value = event.target.value;
    setAuth(value);    
  };

  const handleAuthEmail = async () => {
    // 버튼을 누르자마자 비활성화
    setCountdown(60);
    setHasSentCode(true);
    
    try {
      let target;
      if (loginType === 'email') {
        target = email;
      } else {
        // 전화번호 로그인인 경우 국가코드와 전화번호 조합
        let formattedPhoneNumber = phoneNumber;
        if (phoneNumber.startsWith('0')) {
          formattedPhoneNumber = phoneNumber.substring(1);
        }
        target = countryCode + formattedPhoneNumber;
      }

      const checkResponse = await userAPI.checkUserExists({ loginId: target });
      setUserNameRequired(!checkResponse.data.exists);
      if (checkResponse.data.exists === true && checkResponse.data.isValid === true) {
        setSnackbar({
          open: true,
          message: t('auth_id_already_exists'),
          severity: 'error'
        });
        // 사용자가 이미 존재하는 경우 버튼 상태 복원
        setCountdown(0);
        setHasSentCode(false);
        return;
      } else {
        const codeType = loginType === 'phone' ? 1 : 0;
        await codeAPI.codeGenerate({ target: target, type: codeType });
        setSnackbar({
          open: true,
          message: `${t('reset_password_code_sent')} ${loginType === 'email' ? email : phoneNumber}`,
          severity: 'success'
        });
        // 성공 시에는 이미 설정된 상태 유지
      }
    } catch (error) {
      console.error('인증번호 발송 실패:', error);
      
      // 에러 발생 시 버튼 상태 복원
      setCountdown(0);
      setHasSentCode(false);

      // 에러 메시지가 'code_limit_exceeded_per_day'인 경우 특정 메시지 표시
      if (error?.message === 'code_limit_exceeded_per_day') {
        setSnackbar({
          open: true,
          message: t('auth_code_limit_exceeded_per_day'),
          severity: 'error'
        });
      } else if(error.message.indexOf('code 400') > -1){
        setSnackbar({
          open: true,
          message: t('auth_phone_not_exist'),
          severity: 'error'
        });
      } else{
        setSnackbar({
          open: true,
          message: t('app_error'),
          severity: 'error'
      });
        // const errDetail = error?.response?.data?.message
        //   || error?.message
        //   || (error?.response?.data && JSON.stringify(error.response.data))
        //   || String(error);
        // setSnackbar({
        //   open: true,
        //   message: `${t('app_error')}: ${errDetail}`,
        //   severity: 'error'
        // });
      }
    }
  };

  const handleNext = async () => {
    try {
      let target;
      if (loginType === 'email') {
        target = email;
      } else {
        // 전화번호 로그인인 경우 국가코드와 전화번호 조합
        let formattedPhoneNumber = phoneNumber;
        if (phoneNumber.startsWith('0')) {
          formattedPhoneNumber = phoneNumber.substring(1);
        }
        target = countryCode + formattedPhoneNumber;
      }

      const codeType = loginType === 'phone' ? 1 : 0;
      const response = await codeAPI.codeConfirm({ target: target, type: codeType, key: auth });
      if (response.result === 'ok') {
        setStep(1);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `${t('reset_password_code_invalid')}`,
        severity: 'error'
      });
    }
  };

  const handleClearPassword = () => setPassword('');
  const handleClearPasswordConfirm = () => setPasswordConfirm('');

  const handlePasswordChange = (event) => {
    const value = event.target.value;
    setPassword(value);

    if (!value || !isValidPassword(value)) {
      setPasswordError(true);
      setPasswordErrorMessage(t('auth_invalid_password'));
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
      
      if (passwordConfirm && value !== passwordConfirm) {
        setPasswordConfirmError(true);
        setPasswordConfirmErrorMessage(t('auth_password_mismatch'));
      } else {
        setPasswordConfirmError(false);
        setPasswordConfirmErrorMessage('');
      }
    }
  };

  const handlePasswordConfirmChange = (event) => {
    const value = event.target.value;
    setPasswordConfirm(value);
    if (!value || value.length < 4) {
      setPasswordConfirmError(true);
      setPasswordConfirmErrorMessage(t('auth_invalid_password')); 
    }else if(password !== value){
      setPasswordConfirmError(true);
      setPasswordConfirmErrorMessage(t('auth_password_mismatch'));      
    }else{
      setPasswordConfirmError(false);
      setPasswordConfirmErrorMessage('');
    }
  };

  const handleClearUserName = () => setUserName('');

  // 전화번호 관련 핸들러 추가
  const handlePhoneNumberChange = (event) => {
    const value = event.target.value.replace(/[^0-9]/g, '');
    setPhoneNumber(value);
    if (!value || value.length < 8) {
      setPhoneError(true);
      setPhoneErrorMessage(t('auth_invalid_phone'));
    } else {
      setPhoneError(false);
      setPhoneErrorMessage('');
    }
  };

  const handleUserNameChange = (event) => {
    const value = event.target.value;
    setUserName(value);

    if (!value || value.length < 2) {
      setUserNameError(true);
      setUserNameErrorMessage(t('auth_invalid_name'));
    } else {
      setUserNameError(false);
      setUserNameErrorMessage('');
    }
  };  

  const CustomCheckbox = ({ checked, onChange }) => (
    <Box
      component="img"
      src={checked ? onIcon : offIcon}
      alt="checkbox"
      sx={{
        width: '20px',
        height: '20px',
        cursor: 'pointer',        
      }}
      onClick={() => onChange(!checked)}
    />
  );

  // SNS 로그인 처리 함수
  const handleSNSLogin = useCallback(async (loginData) => {       
    // pushToken을 현재 상태에서 직접 가져오기
    const currentPushToken = localStorage.getItem('pushToken') || '';
    
    try {      
      // 구글 로그인을 위한 별도 API 호출
      const response = await authorizeAPI.signIn({
        grant_type: 'sns',
        sns_type: loginData?.snsType, // SNS provider type (kakao, naver, google, apple)
        sns_id: loginData?.snsId, // SNS id
        push_token: currentPushToken // localStorage에서 가져온 pushToken 사용
      });

      if(response?.access_token) {        
        const appType = localStorage.getItem('appType');
        const decoded = jwt.decode(response.access_token);

        if((appType === 'admin' || appType === 'commander') && decoded.exp){          
          setSnackbar({
            open: true,
            message: t('auth_admin_only_access'),
            severity: 'error'
          });
        } else {          
          localStorage.setItem('access_token', response.access_token);
          localStorage.setItem('refresh_token', response.refresh_token);

          dispatch({
            type: 'info/setAuth',
            payload: {
              accessToken: response.access_token,
              refreshToken: response.refresh_token
            }
          });

          const userResponse = await userAPI.getUserMe(response.access_token);
          localStorage.setItem('meInfo', JSON.stringify(userResponse.data.user));
          // meInfo 변경 이벤트 발생
          window.dispatchEvent(new Event('meInfoChanged'));            
          
          // Send user grade information to WebView
          if (window.ReactNativeWebView) {
            try {
              const meInfo = JSON.parse(localStorage.getItem('meInfo'));
              if (!meInfo) return;

              const settingResponse = await settingAPI.getSettings({userId: meInfo.id});
              // noticePush 값 추출
              let noticePush = true;
              if (settingResponse && settingResponse.data && Array.isArray(settingResponse.data.settings)) {
                const noticeSetting = settingResponse.data.settings.find(s => s.name === 'noticePush');
                noticePush = !(noticeSetting && (noticeSetting.value === false || noticeSetting.value === 'false'));
              }
              let activePush = true;
              if (settingResponse && settingResponse.data && Array.isArray(settingResponse.data.settings)) {
                const activeSetting = settingResponse.data.settings.find(s => s.name === 'activePush');
                activePush = !(activeSetting && (activeSetting.value === false || activeSetting.value === 'false'));
              }
              if (noticePush && activePush) {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                  type: 'subscribeTopics',
                }));
              }
            } catch (e) {
              console.error('Error setting up push notifications:', e);
            }
          }
          
          navigate(`/?appType=${appType}`, { replace: true });
        }
      }
        } catch (error) {            
      setEmail(loginData?.email);      
      setUserName(loginData?.name);
      setSnsId(loginData?.snsId);
      setSnsType(loginData?.snsType);     
    }
  }, [t, dispatch, navigate]);

  const handleRegister = async () => {
    try {
      let loginId;
      if (loginType === 'email') {
        loginId = email;
      } else {
        // 전화번호 로그인인 경우 국가코드와 전화번호 조합
        let formattedPhoneNumber = phoneNumber;
        if (phoneNumber.startsWith('0')) {
          formattedPhoneNumber = phoneNumber.substring(1);
        }
        loginId = countryCode + formattedPhoneNumber;
      }
      
      const response = await userAPI.registerUser({
        loginId: loginId,
        loginPwd: password,
        name: userName,
        snsType: snsType,
        snsId: snsId
      });
     
      if (response.result === 'created') {
        // 회원가입 성공 후 자동 로그인
        const loginResponse = snsType
        ? await authorizeAPI.signIn({
            grant_type: 'sns',
            sns_type: snsType, // SNS provider type
            sns_id: snsId // SNS id
          })
        : await authorizeAPI.signIn({
            grant_type: 'password',
            username: loginId,
            password: password
          });
        
        if (loginResponse.access_token) {
          const appType = localStorage.getItem('appType');
          const decoded = jwt.decode(loginResponse.access_token);

          if ((appType === 'admin' || appType === 'commander') && decoded.exp) {
            setSnackbar({
              open: true,
              message: t('auth_admin_only_access'),
              severity: 'error'
            });
          } else {
            localStorage.setItem('access_token', loginResponse.access_token);
            localStorage.setItem('refresh_token', loginResponse.refresh_token);

            dispatch({
              type: 'info/setAuth',
              payload: {
                accessToken: loginResponse.access_token,
                refreshToken: loginResponse.refresh_token
              }
            });

            const userResponse = await userAPI.getUserMe(loginResponse.access_token);
            localStorage.setItem('meInfo', JSON.stringify(userResponse.data.user));

            navigate(`/?appType=${appType}`, { replace: true });
          }
        }
      }
    } catch (error) {    
      console.log(error);
      if (error.message?.includes('already_exists')) {
        setSnackbar({
          open: true,
          message: t('auth_id_already_exists'),
          severity: 'error'
        });
      } else {
        // const errDetail = error?.response?.data?.message
        //   || error?.message
        //   || (error?.response?.data && JSON.stringify(error.response.data))
        //   || String(error);
        setSnackbar({
          open: true,
          message: t('auth_sign_up_error'),
          // message: `${t('auth_sign_up_error')}: ${errDetail}`,
          severity: 'error'
        });
      }
      setStep(location?.state?.step || 0);
      setAuth('');      
      setPassword('');
      setPasswordConfirm('');
      setUserName('');  
    }
  };

  return (  
    <Box sx={{
      display: 'flex',
        width: '100%',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'center',
        alignItems: 'center',        
        bgcolor: { xs: '#FFF', sm: '#FDFBFE' },
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: '0',
        paddingBottom: '32px',
        overflow: 'hidden'
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        height: '54px', 
        px: 2, 
        borderBottom: '1px solid rgba(0,0,0,0.10)', 
        width: '100%' 
      }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '15px', color: '#211F20',  }}>
          {t('auth_sign_up')}
        </Typography>
      </Box>
      {step === 0 ? 
      <>
        <Box sx={{  
          display: 'flex',
          padding: '64px 20px 32px 20px',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '50px',
          flex: '1 0 0',
          alignSelf: 'stretch'
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '40px',
            alignSelf: 'stretch'
          }}>           
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '20px',
                alignSelf: 'stretch'
              }}
            >            
              {loginType === 'email' ? (
                <FormControl sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                  alignSelf: 'stretch'
                }}>
                  <FormLabel htmlFor="email" sx={{color: 'rgba(0, 0, 0, 0.60)', textAlign: 'center', fontSize: '15px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal' }}>{t('user_email')}</FormLabel>
                  <TextField
                      required
                      fullWidth
                      id="email"
                      placeholder={t('form_email_placeholder')}
                      name="email"                  
                      variant="outlined"
                      error={emailError}
                      helperText={emailErrorMessage}
                      color={passwordError ? 'error' : 'primary'}
                      value={email}
                      onChange={handleEmailChange}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '38px',
                          borderRadius: '16px',
                          border: '1px solid rgba(0, 0, 0, 0.10)',
                          backgroundColor: 'rgba(0, 0, 0, 0.03)',
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputBase-input': {
                          padding: '0 16px',
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }}
                      InputProps={{
                        endAdornment: email && (
                          <InputAdornment position="end">
                            <IconButton onClick={handleClearEmail} edge="end" size="small">
                              <Box
                                component="img"
                                src={closeIcon}
                                sx={{ width: '20px', height: '20px', aspectRatio: '1/1' }}
                              />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                  />
                </FormControl>
              ) : (
                <>
                  <FormControl sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                    alignSelf: 'stretch'
                  }}>
                    <FormLabel sx={{color: 'rgba(0, 0, 0, 0.60)', textAlign: 'center', fontSize: '15px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal' }}>{t('country_region')}</FormLabel>
                    <CustomSelect
                      name="country"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      options={[
                        { value: '81', label: t('japan_code') },                      
                        { value: '886', label: t('taiwan_code') },
                        { value: '62', label: t('indonesia_code') },
                        { value: '82', label: t('korea_code') }
                      ]}            
                      sx={{                        
                        width: '100%',
                        height: '38px',
                        padding: '0px 20px',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        alignSelf: 'stretch',
                        '& .MuiSelect-select': {
                          padding: '0',
                          textAlign: 'left'
                        }
                      }}
                      required
                    />
                  </FormControl>
                  <FormControl sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                    alignSelf: 'stretch'
                  }}>
                    <FormLabel htmlFor="phone" sx={{color: 'rgba(0, 0, 0, 0.60)', textAlign: 'center', fontSize: '15px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal' }}>{t('user_phone')}</FormLabel>
                    <TextField
                      required
                      fullWidth
                      id="phone"
                      placeholder={t('auth_phone_placeholder')}
                      name="phone"                  
                      variant="outlined"
                      error={phoneError}
                      helperText={phoneErrorMessage}
                      color={phoneError ? 'error' : 'primary'}
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '38px',
                          borderRadius: '16px',
                          border: '1px solid rgba(0, 0, 0, 0.10)',
                          backgroundColor: 'rgba(0, 0, 0, 0.03)',
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputBase-input': {
                          padding: '0 16px',
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }}
                      InputProps={{
                        endAdornment: phoneNumber && (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setPhoneNumber('')} edge="end" size="small">
                              <Box
                                component="img"
                                src={closeIcon}
                                sx={{ width: '20px', height: '20px', aspectRatio: '1/1' }}
                              />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </FormControl>
                </>
              )}
              <FormControl sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                alignSelf: 'stretch'
              }}>
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', alignSelf: 'stretch'}}>
                  <FormLabel htmlFor="auth" sx={{color: 'rgba(0, 0, 0, 0.60)', textAlign: 'center', fontSize: '15px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal' }}>{t('reset_password_verification_code')}</FormLabel>
                  <Box sx={{display: 'flex', width: '100%', height: '38px', gap: '10px'}}>
                    <TextField
                        required
                        fullWidth
                        id="auth"
                        placeholder={t('reset_password_code_placeholder')}
                        name="auth"                  
                        variant="outlined"                 
                        color={'primary'}
                        value={auth}
                        onChange={handleAuthChange}
                        sx={{                   
                          height: '38px',                   
                          '& .MuiOutlinedInput-root': {
                            display: 'flex',
                            height: '38px',
                            borderRadius: '16px',                        
                            alignItems: 'center',
                            gap: '10px',
                            flex: '1 0 0',
                            border: '1px solid rgba(0, 0, 0, 0.10)',
                            backgroundColor: 'rgba(0, 0, 0, 0.03)',
                            '& fieldset': {
                              border: 'none'
                            },
                            '&:hover fieldset': {
                              border: 'none'
                            },
                            '&.Mui-focused fieldset': {
                              border: 'none'
                            }
                          },
                          '& .MuiInputBase-input': {
                            padding: '0 16px',
                            display: 'flex',
                            alignItems: 'center'
                          }
                        }}
                    />      
                    <CustomButton 
                      variant="normal"
                      disabled={countdown > 0 || (loginType === 'email' ? emailError || !email : phoneError || !phoneNumber)}
                      onClick={handleAuthEmail}                      
                    >
                      {countdown > 0 ? `${t('reset_password_resend')}(${countdown}s)` : (hasSentCode ? t('reset_password_resend_code') : t('reset_password_send_code'))}
                    </CustomButton>
                  </Box>       
                </Box>
              </FormControl>     
            </Box>                    
          </Box>
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '40px',
          alignSelf: 'stretch'
        }}>
          <Box sx={{display: 'flex', padding: '0px 20px', flexDirection: 'column', alignItems: 'center', gap: '20px', alignSelf: 'stretch'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: '10px', alignSelf: 'stretch'}}>
              <CustomCheckbox
                checked={agree}
                onChange={(checked) => setAgree(checked)}
              />
              <Box sx={{display: 'flex', alignItems: 'center', gap: '2px', flex: '1 0 0', flexWrap: 'wrap'}}>
                <Typography sx={{color: 'rgba(0, 0, 0, 0.60)', fontSize: '12px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal', letterSpacing: '-0.36px'}}>{t('agree_terms')}</Typography>                
                <Typography onClick={()=> navigate('/auth/terms', {state: {type: type[0]}})} sx={{color: '#FF43AA', fontSize: '12px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal', letterSpacing: '-0.36px', textDecorationLine: 'underline', textDecorationStyle: 'solid', textDecorationSkipInk: 'none', textDecorationThickness: 'auto', textUnderlineOffset: 'auto', textUnderlinePosition: 'from-font', }}>"{t('setting_terms')}"</Typography>                
                <Typography sx={{color: 'rgba(0, 0, 0, 0.60)', fontSize: '12px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal', letterSpacing: '-0.36px'}}>{'and'}</Typography>                
                <Typography onClick={()=> navigate('/auth/terms', {state: {type: type[1]}})} sx={{color: '#FF43AA', fontSize: '12px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal', letterSpacing: '-0.36px', textDecorationLine: 'underline', textDecorationStyle: 'solid', textDecorationSkipInk: 'none', textDecorationThickness: 'auto', textUnderlineOffset: 'auto', textUnderlinePosition: 'from-font', }}>"{t('privacy_title')}"</Typography>
              </Box>  
            </Box>
            <CustomButton 
              variant={auth && agree ? 'normal' : 'gray'}
              disabled={!auth || !agree || (loginType === 'phone' && (phoneError || !phoneNumber))}
              onClick={handleNext}
              sx={{ alignSelf: 'stretch' }}
            >
              {t('next')}
            </CustomButton>
            <Typography sx={{color: 'rgba(0, 0, 0, 0.60)', textAlign: 'center', fontSize: '15px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal'}}>{t('other_sign_up')}</Typography>          
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '40px', alignSelf: 'stretch'}}>
              <Box
                      component="img"
                      src={loginType === 'email' ? phoneIcon : messageIcon}
                      sx={{ 
                        width: 50,
                        height: 50,
                        aspectRatio: 1/1,
                        cursor: 'pointer'
                      }}
                      onClick={() => setLoginType(loginType === 'email' ? 'phone' : 'email')}
                  />
                  {platform === 'android' && (
                    <Box
                      component="img"
                      src={googleIcon}
                      sx={{ width: 50, height: 50, aspectRatio: 1/1, cursor: 'pointer' }}
                      onClick={() => {
                        if (window.ReactNativeWebView) {
                          window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'googleLogin' }));
                        }
                      }}
                    />
                  )}
                  {platform === 'ios' && (
                    <Box
                      component="img"
                      src={appleIcon}
                      sx={{ width: 50, height: 50, aspectRatio: 1/1, cursor: 'pointer' }}
                      onClick={() => {
                        if (window.ReactNativeWebView) {
                          window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'appleLogin' }));
                        }
                      }}
                    />
                  )}
            </Box>          
          </Box>
        </Box>
      </>
      :
      <>
        <Box sx={{display: 'flex', padding: '64px 20px 32px 20px', flexDirection: 'column', alignItems: 'center', gap: '50px', flex: '1 0 0', alignSelf: 'stretch'}}>
          <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '40px', alignSelf: 'stretch'}}>
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '40px', alignSelf: 'stretch'}}>
              <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', alignSelf: 'stretch'}}>              
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', alignSelf: 'stretch'}}>
                  <Typography sx={{color: 'rgba(0, 0, 0, 0.60)', textAlign: 'center', fontSize: '15px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal'}}>{t('account')}</Typography>
                  <Typography sx={{color: '#211F20', textAlign: 'center', fontSize: '15px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal'}}>{email}</Typography>
                </Box>
                {snsId && (
                  <Typography sx={{ color: 'rgba(0, 0, 0, 0.60)', textAlign: 'center', fontSize: '14px', fontStyle: 'normal', fontWeight: '500', lineHeight: '1.4', alignSelf: 'stretch' }}>
                    {t('signup_sns_password_guide')}
                  </Typography>
                )}
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', alignSelf: 'stretch'}}>
                  <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', alignSelf: 'stretch'}}>
                    <TextField
                      required
                      fullWidth
                      id="password"
                      placeholder={t('form_password')}
                      type="password"
                      name="password"                  
                      variant="outlined"
                      value={password}
                      onChange={handlePasswordChange}
                      error={passwordError}
                      helperText={passwordErrorMessage}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          display: 'flex',
                          height: '38px',
                          padding: '0px 16px',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          alignSelf: 'stretch',
                          borderRadius: '16px',
                          border: '1px solid rgba(0, 0, 0, 0.10)',
                          backgroundColor: 'rgba(0, 0, 0, 0.03)',
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputBase-input': {
                          padding: '0 16px',
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }}
                      InputProps={{
                        endAdornment: password && (
                            <InputAdornment position="end">
                                <IconButton onClick={handleClearPassword} edge="end" size="small">
                                    <Box
                                        component="img"
                                        src={closeIcon}
                                        sx={{ width: '20px', height: '20px', aspectRatio: '1/1' }}
                                    />
                                </IconButton>
                            </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      required
                      fullWidth
                      id="passwordConfirm"
                      placeholder={t('reset_password_confirm')}
                      type="password"
                      name="passwordConfirm"     
                      value={passwordConfirm}
                      onChange={handlePasswordConfirmChange}             
                      error={passwordConfirmError}
                      helperText={passwordConfirmErrorMessage}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          display: 'flex',
                          height: '38px',
                          padding: '0px 16px',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          alignSelf: 'stretch',
                          borderRadius: '16px',
                          border: '1px solid rgba(0, 0, 0, 0.10)',
                          backgroundColor: 'rgba(0, 0, 0, 0.03)',
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputBase-input': {
                          padding: '0 16px',
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }}
                      InputProps={{
                        endAdornment: passwordConfirm && (
                            <InputAdornment position="end">
                                <IconButton onClick={handleClearPasswordConfirm} edge="end" size="small">
                                    <Box
                                        component="img"
                                        src={closeIcon}
                                        sx={{ width: '20px', height: '20px', aspectRatio: '1/1' }}
                                    />
                                </IconButton>
                            </InputAdornment>
                        ),
                      }}/>    
                  </Box>
                  <Typography sx={{color: 'rgba(0, 0, 0, 0.60)', textAlign: 'center', fontSize: '12px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal', letterSpacing: '-0.36px'}}>{t('auth_invalid_password')}</Typography>                                
                </Box>               
              </Box>
              {userNameRequired &&
              <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', alignSelf: 'stretch'}}>     
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', alignSelf: 'stretch'}}>
                  <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', alignSelf: 'stretch'}}>
                    <FormLabel htmlFor="userName" sx={{color: 'rgba(0, 0, 0, 0.60)', textAlign: 'center', fontSize: '15px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal' }}>{t('user_name')}</FormLabel>
                    <TextField
                          required
                          fullWidth
                          id="userName"
                          placeholder={t('user_name')}
                          name="userName"                  
                          variant="outlined"                 
                          color={'primary'}
                          value={userName}
                          onChange={handleUserNameChange}
                          error={userNameError}
                          helperText={userNameErrorMessage}
                          sx={{                   
                            height: '38px',                   
                            '& .MuiOutlinedInput-root': {
                              display: 'flex',
                              height: '38px',
                              borderRadius: '16px',                        
                              alignItems: 'center',
                              gap: '10px',
                              flex: '1 0 0',
                              border: '1px solid rgba(0, 0, 0, 0.10)',
                              backgroundColor: 'rgba(0, 0, 0, 0.03)',
                              '& fieldset': {
                                border: 'none'
                              },
                              '&:hover fieldset': {
                                border: 'none'
                              },
                              '&.Mui-focused fieldset': {
                                border: 'none'
                              }
                            },
                            '& .MuiInputBase-input': {
                              padding: '0 16px',
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }}
                          InputProps={{
                            endAdornment: userName && (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleClearUserName} edge="end" size="small">
                                        <Box
                                            component="img"
                                            src={closeIcon}
                                            sx={{ width: '20px', height: '20px', aspectRatio: '1/1' }}
                                        />
                                    </IconButton>
                                </InputAdornment>
                            ),
                          }}
                        />
                  </Box>
                </Box>
              </Box>
              }
            </Box>
          </Box>
        </Box>
        <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: '20px', alignSelf: 'stretch'}}>
          <Box sx={{display: 'flex', padding: '0px 20px', flexDirection: 'column', alignItems: 'center', gap: '20px', alignSelf: 'stretch'}}>
            {snsId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', alignSelf: 'stretch' }}>
                <CustomCheckbox
                  checked={agreeSns}
                  onChange={(checked) => setAgreeSns(checked)}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', flex: '1 0 0', flexWrap: 'wrap' }}>
                  <Typography sx={{ color: 'rgba(0, 0, 0, 0.60)', fontSize: '12px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal', letterSpacing: '-0.36px' }}>{t('agree_terms')}</Typography>
                  <Typography component="span" onClick={() => navigate('/auth/terms', { state: { type: type[0] } })} sx={{ color: '#FF43AA', fontSize: '12px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal', letterSpacing: '-0.36px', textDecorationLine: 'underline', cursor: 'pointer' }}>&quot;{t('setting_terms')}&quot;</Typography>
                  <Typography sx={{ color: 'rgba(0, 0, 0, 0.60)', fontSize: '12px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal', letterSpacing: '-0.36px' }}> and </Typography>
                  <Typography component="span" onClick={() => navigate('/auth/terms', { state: { type: type[1] } })} sx={{ color: '#FF43AA', fontSize: '12px', fontStyle: 'normal', fontWeight: '500', lineHeight: 'normal', letterSpacing: '-0.36px', textDecorationLine: 'underline', cursor: 'pointer' }}>&quot;{t('privacy_title')}&quot;</Typography>
                </Box>
              </Box>
            )}
            <CustomButton 
              variant={password.length >= 6 && password === passwordConfirm && (!snsId || agreeSns) ? 'normal' : 'gray'}
              disabled={password.length < 6 || password !== passwordConfirm || passwordError || passwordConfirmError || userNameError || (snsId && !agreeSns)}
              onClick={handleRegister}
              sx={{ alignSelf: 'stretch' }}
            >
              {t('common_confirm')}
            </CustomButton>
          </Box>
        </Box>
      </>
      }

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          // AOS 웹뷰에서 상단에 안전하게 표시되도록 여백 조정
          mt: { xs: '60px', sm: '20px' }
        }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ 
            width: '100%', 
            borderRadius: '8px',
            // AOS 웹뷰에서 더 잘 보이도록 스타일 강화
            fontSize: '14px',
            fontWeight: 500,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}